import {
  IconDotsVertical,
  IconPackage,
  IconTrash,
  IconUser,
  IconUserPlus,
  IconX,
} from "@tabler/icons-react";
import { ReactNode, ComponentType } from "react";
import { Button } from "./Button/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./DropdownMenu";
import { cn, formatCreatedAt } from "../utils";
import { NotificationDto, NotificationType } from "@repo/api/notification";
import {
  notificationQueryKeys,
  removeNotificationFromCache,
  useDeleteNotificationMutation,
} from "../queries/notification-queries";
import { useAcceptInviteMutation } from "../queries/invite-queries";
import { queryClient } from "../queries/query-client";
import { userQueryKeys } from "../queries/user-queries";
import { paths } from "@/App";

type NotificationProps = {
  icon: ReactNode;
  subtext: string;
  message: string;
  createdAt: string;
  topRightSlot?: ReactNode;
  actionsChildren?: ReactNode;
  isUnseen?: boolean;
  className?: string;
};

const NOTIFICATION_TYPE_CONFIG: Record<
  NotificationType,
  { icon: ReactNode; subtext: string }
> = {
  INVITE_RECEIVED: {
    icon: <IconUserPlus size={18} strokeWidth={1.5} />,
    subtext: "Invite received",
  },
  TEAM_MEMBER_JOINED: {
    icon: <IconUser size={18} strokeWidth={1.5} />,
    subtext: "User joined",
  },
  DFY_ORDER_DELIVERED: {
    icon: <IconPackage size={18} strokeWidth={1.5} />,
    subtext: "Order delivered",
  },
};

const Notification = ({
  icon,
  subtext,
  message,
  createdAt,
  topRightSlot,
  actionsChildren,
  isUnseen = false,
  className,
}: NotificationProps) => {
  return (
    <div
      className={cn(
        "relative flex items-start gap-4 px-5 py-4",
        isUnseen && "bg-linear-to-br from-primary-100 to-secondary-100",
        className,
      )}
    >
      <div
        className="flex min-h-10 min-w-10 items-center justify-center rounded-[8px] border border-transparent [&_svg]:stroke-[url(#icon-gradient)]"
        style={{
          background:
            "linear-gradient(to bottom right, var(--color-primary-100), var(--color-secondary-100)) padding-box, linear-gradient(to bottom right, color-mix(in srgb, var(--color-primary-400) 10%, transparent), color-mix(in srgb, var(--color-secondary-400) 10%, transparent)) border-box",
        }}
      >
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <div className="relative flex w-full items-start justify-between">
          <p className="mr-6 line-clamp-3 w-full text-sm font-semibold text-text-primary">
            {message}
          </p>
          <div className="absolute -top-1 -right-1">{topRightSlot}</div>
        </div>
        <div className="mt-1 flex items-baseline gap-2 pr-4">
          <span className="shrink-0 text-xs whitespace-nowrap text-text-secondary">
            {formatCreatedAt(createdAt, null)} • {subtext}
          </span>
        </div>

        {actionsChildren && (
          <div className="mt-4 mb-1 flex items-center gap-2">
            {actionsChildren}
          </div>
        )}
      </div>
    </div>
  );
};

type NotificationMenuMode = "close" | "dropdown";

type NotificationItemComponent = ComponentType<{
  notification: NotificationDto;
  onDismiss?: () => void;
  isUnseen?: boolean;
  menuMode?: NotificationMenuMode;
}>;

const NOTIFICATION_RENDERERS: Record<
  NotificationType,
  NotificationItemComponent
> = {
  INVITE_RECEIVED: InviteReceivedNotification,
  TEAM_MEMBER_JOINED: TeamMemberJoinedNotification,
  DFY_ORDER_DELIVERED: DfyOrderDeliveredNotification,
};

export function NotificationItem({
  notification,
  onDismiss,
  isUnseen,
  menuMode,
}: {
  notification: NotificationDto;
  onDismiss?: () => void;
  isUnseen?: boolean;
  menuMode?: NotificationMenuMode;
}) {
  const Renderer = NOTIFICATION_RENDERERS[notification.type];
  return (
    <Renderer
      notification={notification}
      onDismiss={onDismiss}
      isUnseen={isUnseen}
      menuMode={menuMode}
    />
  );
}

function NotificationShell({
  notification,
  message,
  actionsChildren,
  onDismiss,
  isUnseen,
  menuMode = "dropdown",
}: {
  notification: NotificationDto;
  message: string;
  actionsChildren?: ReactNode;
  onDismiss?: () => void;
  isUnseen?: boolean;
  menuMode?: NotificationMenuMode;
}) {
  const { icon, subtext } = NOTIFICATION_TYPE_CONFIG[notification.type];
  const { mutate: deleteNotification } = useDeleteNotificationMutation();

  const topRightSlot =
    menuMode === "close" ? (
      <Button
        type="button"
        onClick={() => onDismiss?.()}
        aria-label="Dismiss notification"
        className="-mt-0.5 -mr-2 flex cursor-pointer items-center justify-center rounded-md text-text-secondary hover:text-text-primary"
        variant="ghost"
        size="iconXs"
      >
        <IconX size={14} />
      </Button>
    ) : (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            aria-label="Notification options"
            variant="ghost"
            size="iconXs"
            className="-mr-2 min-w-8"
          >
            <IconDotsVertical className="text-text-primary" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-52">
          <DropdownMenuItem
            className="text-error hover:text-error!"
            onSelect={() => {
              deleteNotification(notification.externalId);
              onDismiss?.();
            }}
          >
            <IconTrash size={18} />
            Delete notification
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );

  return (
    <Notification
      icon={icon}
      subtext={subtext}
      message={message}
      createdAt={notification.createdAt}
      isUnseen={isUnseen ?? notification.seenAt === null}
      topRightSlot={topRightSlot}
      actionsChildren={actionsChildren}
    />
  );
}

function InviteReceivedNotification({
  notification,
  onDismiss,
  isUnseen,
  menuMode,
}: {
  notification: NotificationDto;
  onDismiss?: () => void;
  isUnseen?: boolean;
  menuMode?: NotificationMenuMode;
}) {
  const invite = notification.invite;
  const teamName = invite?.teamName ?? "Unknown Team";
  const inviteExternalId = invite?.externalId ?? null;

  const { mutate: deleteNotification } = useDeleteNotificationMutation();
  const { mutate: acceptInvite, isPending: isAcceptInvitePending } =
    useAcceptInviteMutation({
      inviteExternalId: inviteExternalId ?? "",
      onSuccess: async () => {
        removeNotificationFromCache(notification.externalId);
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: notificationQueryKeys.listNotifications(),
          }),
          queryClient.invalidateQueries({
            queryKey: userQueryKeys.all(),
            type: "all",
          }),
        ]);
      },
    });

  return (
    <NotificationShell
      notification={notification}
      message={`You have been invited to join ${teamName}.`}
      onDismiss={onDismiss}
      isUnseen={isUnseen}
      menuMode={menuMode}
      actionsChildren={
        <>
          <Button
            size="sm"
            variant="default"
            onClick={() => {
              if (!inviteExternalId || isAcceptInvitePending) {
                return;
              }
              acceptInvite();
              onDismiss?.();
            }}
          >
            Accept
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              deleteNotification(notification.externalId);
              onDismiss?.();
            }}
          >
            Decline
          </Button>
        </>
      }
    />
  );
}

function TeamMemberJoinedNotification({
  notification,
  onDismiss,
  isUnseen,
  menuMode,
}: {
  notification: NotificationDto;
  onDismiss?: () => void;
  isUnseen?: boolean;
  menuMode?: NotificationMenuMode;
}) {
  const message = notification.message ?? "A new member has joined the team.";

  return (
    <NotificationShell
      notification={notification}
      message={message}
      onDismiss={onDismiss}
      isUnseen={isUnseen}
      menuMode={menuMode}
    />
  );
}

function DfyOrderDeliveredNotification({
  notification,
  onDismiss,
  isUnseen,
  menuMode,
}: {
  notification: NotificationDto;
  onDismiss?: () => void;
  isUnseen?: boolean;
  menuMode?: NotificationMenuMode;
}) {
  const dfyOrder = notification.dfyOrder;
  const message = notification.message ?? "Your order has been delivered.";

  return (
    <NotificationShell
      notification={notification}
      message={message}
      onDismiss={onDismiss}
      isUnseen={isUnseen}
      menuMode={menuMode}
      actionsChildren={
        dfyOrder && (
          // Plain link: toasts render outside the router, so useNavigate is unavailable.
          <Button size="sm" variant="default" asChild>
            <a
              href={paths.dfyOrder(
                dfyOrder.teamExternalId,
                dfyOrder.externalId,
              )}
              onClick={() => onDismiss?.()}
            >
              View order
            </a>
          </Button>
        )
      }
    />
  );
}

export default Notification;
